import React from "react";

import { mdiChevronDown, mdiDelete, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useTranslation } from "react-i18next";

import ConditionalRenderEditor from "./components/ConditionalRenderEditor";
import Button from "../../components/button/Button";
import ModuleLanguage from "../../components/utils/ModuleLanguage";
import TranslateFormInput from "../../components/utils/translateForm/TranslateFormInput";
import TranslateFormMultipleInputs from "../../components/utils/translateForm/TranslateFormMultipleInputs";

import type { JSX } from "react";

import type {
  ConditionRender,
  FAQComponent,
  FAQQuestion,
} from "../../api/ProjectAPI.types";

interface FAQComponentFormProps {
  faqComponent: FAQComponent;
  setFAQComponent: React.Dispatch<
    React.SetStateAction<FAQComponent | undefined>
  >;
}

export default function FAQComponentForm({
  faqComponent,
  setFAQComponent,
}: FAQComponentFormProps): JSX.Element {
  const { t } = useTranslation("editor-platform");

  const handleUpdateItem = (updatedFaq: FAQComponent): void => {
    setFAQComponent((prevTranslations) => ({
      ...prevTranslations,
      ...updatedFaq,
    }));
  };

  const handleAddLanguage = (updatedTranslation: FAQComponent): void => {
    setFAQComponent((prevTranslations) => ({
      ...prevTranslations,
      ...updatedTranslation,
    }));
  };

  const handleTranslationsChange = (
    translations: FAQComponent["translations"],
  ): void => {
    setFAQComponent((prevTranslations) => {
      if (!prevTranslations) return prevTranslations;
      return {
        ...prevTranslations,
        translations,
      };
    });
  };

  const handleAddQuestion = (): void => {
    const newRank = faqComponent.translations[0]?.faq_questions?.length
      ? faqComponent.translations[0].faq_questions.length + 1
      : 1;

    const newQuestion: FAQQuestion = {
      rank: newRank,
      question: "",
      answer: "",
    };

    setFAQComponent((prevItem) => {
      if (!prevItem) return prevItem;
      return {
        ...prevItem,
        translations: prevItem.translations.map((translation) => ({
          ...translation,
          faq_questions: [...(translation.faq_questions || []), newQuestion],
        })),
      };
    });
  };

  const handleRemoveQuestion = (index: number): void => {
    setFAQComponent((prevItem) => {
      if (!prevItem) return prevItem;
      return {
        ...prevItem,
        translations: prevItem.translations.map((translation) => ({
          ...translation,
          faq_questions: translation.faq_questions
            .filter((_, questionIndex) => questionIndex !== index)
            .map((question, questionIndex) => ({
              ...question,
              rank: questionIndex + 1,
            })),
        })),
      };
    });
  };

  const handleConditionsToggle = (value: boolean): void => {
    setFAQComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        params: {
          questionCount: prevComponent.params?.questionCount ?? 0,
          withTitle: prevComponent.params?.withTitle ?? false,
          withCTAButton: prevComponent.params?.withCTAButton ?? false,
          withCTASubtitle: prevComponent.params?.withCTASubtitle ?? false,
          has_conditions: value,
          conditional_render: value
            ? (prevComponent.params?.conditional_render ?? [])
            : undefined,
        },
      };
    });
  };

  const handleConditionsChange = (value: ConditionRender[]): void => {
    setFAQComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        params: {
          questionCount: prevComponent.params?.questionCount ?? 0,
          withTitle: prevComponent.params?.withTitle ?? false,
          withCTAButton: prevComponent.params?.withCTAButton ?? false,
          withCTASubtitle: prevComponent.params?.withCTASubtitle ?? false,
          has_conditions: prevComponent.params?.has_conditions ?? false,
          conditional_render: value,
        },
      };
    });
  };

  const isDeleteDisabled =
    faqComponent.translations[0]?.faq_questions &&
    faqComponent.translations[0]?.faq_questions?.length <= 1;

  const customNewLang = {
    title: "",
    faq_questions: Array.from({
      length: faqComponent.translations[0]?.faq_questions?.length ?? 1,
    }).map((_, index) => ({
      rank: index + 1,
      question: "",
      answer: "",
    })),
    cta: {
      label: "",
      subtext: "",
      url: "",
    },
  };

  const sortedQuestions = faqComponent.translations[0]?.faq_questions?.sort(
    (a, b) => a.rank - b.rank,
  );

  return (
    <div className="bg-card mt-[1.25rem] rounded-[0.234375rem] p-[1.875rem] shadow">
      <div className="divide-border max-w-screen-xl divide-y">
        <div className="editor-form-section py-4!">
          <ModuleLanguage
            hasSlug
            customNewLang={customNewLang}
            item={faqComponent}
            onAddLanguage={handleAddLanguage}
            onUpdate={handleUpdateItem}
          />
        </div>
        <div className="editor-form-section">
          <TranslateFormInput
            fieldName="title"
            id="faq-title"
            inputClassName="!w-full"
            labelClassName="!w-[175px] shrink-0"
            maxLength="128"
            onChange={handleTranslationsChange}
            title={t("faqComponentPage.title")}
            translations={faqComponent.translations}
            translationsIndexes={faqComponent.translations_indexes}
          />
        </div>
        <div className="editor-form-section">
          <div className="flex flex-col">
            {sortedQuestions?.map((question, index) => (
              <Accordion key={question.rank} defaultExpanded>
                <AccordionSummary
                  className="flex-row-reverse gap-[0.78125rem] px-4!"
                  expandIcon={<Icon path={mdiChevronDown} size={1.25} />}
                >
                  <div className="flex w-full items-center justify-between">
                    <h3 className="text-[0.9375rem] font-semibold">
                      {t("faqComponentPage.questionTitle", {
                        index: index + 1,
                      })}
                    </h3>
                    {!isDeleteDisabled && (
                      <div
                        className="text-foreground hover:text-destructive cursor-pointer"
                        onClick={(): void => handleRemoveQuestion(index)}
                      >
                        <Icon path={mdiDelete} size={0.78125} />
                      </div>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <TranslateFormMultipleInputs
                    key={`question-${question.rank}`}
                    fieldName={`faq_questions[${index}]`}
                    item={faqComponent}
                    onChange={handleTranslationsChange}
                    subFieldList={[
                      {
                        key: "question",
                        label: t("faqComponentPage.question"),
                        maxLength: 200,
                      },
                      {
                        key: "answer",
                        label: t("faqComponentPage.answer"),
                        type: "markdown",
                      },
                    ]}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </div>

          <div className="my-[1.5625rem] w-full">
            <Button
              isFullWidth
              className="font-semibold"
              onClick={(): void => handleAddQuestion()}
              size="sm"
              type="button"
              variant="secondary"
              isDisabled={
                faqComponent.translations[0]?.faq_questions
                  ? faqComponent.translations[0]?.faq_questions?.length >= 10
                  : undefined
              }
            >
              <Icon path={mdiPlus} size={0.78125} />
              {t("faqComponentPage.addQuestion")}
            </Button>
          </div>
        </div>
        <div className="editor-form-section">
          <TranslateFormMultipleInputs
            fieldName="cta"
            item={faqComponent}
            onChange={handleTranslationsChange}
            title={t("faqComponentPage.cta.title")}
            subFieldList={[
              { key: "label", label: t("faqComponentPage.cta.label") },
              { key: "subtext", label: t("faqComponentPage.cta.subText") },
              { key: "url", label: t("faqComponentPage.cta.url") },
            ]}
          />
        </div>
        <div className="editor-form-section mt-[1.5625rem]">
          <ConditionalRenderEditor
            componentParams={faqComponent.params}
            onChangeConditions={handleConditionsChange}
            onChangeConditionsToggle={handleConditionsToggle}
          />
        </div>
      </div>
    </div>
  );
}
